import { useCallback, useEffect, useRef, useState } from 'react';
import {
  listUsers, issueCode, getStatus, cancelCode, listDevices, renameDevice,
  revokeDevice, listAudit, createOperationId, pendingOperations,
} from '../../../../../services/TabletPairingService';

const readers = { users: listUsers, devices: listDevices, audit: listAudit };
const emptyPage = () => ({ items: [], next_cursor: null, loading: false, loaded: false, error: null });
const emptyPages = () => ({ users: emptyPage(), devices: emptyPage(), audit: emptyPage() });

// Only copy the documented safe error projection. Never forward raw errors to
// Redux, snackbars, reporting, logs, DOM attributes or an error boundary.
export function safeError(error) {
  const text = (value, fallback = null) => typeof value === 'string' ? value : fallback;
  return {
    status: Number(error?.status) || null,
    code: text(error?.code, 'REQUEST_FAILED'),
    message: text(error?.message, 'The request could not be completed. Please try again.'),
    recovery_action: text(error?.recovery_action, 'none'),
    request_id: text(error?.request_id),
    retryAfter: Math.max(0, Number(error?.retryAfter) || 0),
    device_id: text(error?.device_id),
  };
}

export function useTabletPairing(restaurantId, scope) {
  const [pages, setPages] = useState(emptyPages);
  const [operation, setOperation] = useState(null);
  const [recoveryReady, setRecoveryReady] = useState(false);
  const [pairing, setPairing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [visible, setVisible] = useState(() => document.visibilityState !== 'hidden');
  const [now, setNow] = useState(Date.now);
  const mounted = useRef(true);
  const epoch = useRef(0);
  const locks = useRef(false);
  const requests = useRef(new Set());
  const pageVersions = useRef({});
  const pageLoading = useRef({});
  const operationRef = useRef(null);
  const pairingRef = useRef(null);
  const visibleRef = useRef(visible);
  const blockedRef = useRef(false);
  const cooldownRef = useRef(0);
  const statusError = useRef(false);

  const report = useCallback((err) => {
    const safe = safeError(err);
    if (!mounted.current) return safe;
    setError(safe);
    if (safe.status === 401 || safe.code === 'MANAGEMENT_FORBIDDEN') blockedRef.current = true;
    if (safe.retryAfter) cooldownRef.current = Date.now() + safe.retryAfter * 1000;
    return safe;
  }, []);

  const invalidate = useCallback(() => {
    epoch.current += 1;
    requests.current.forEach((controller) => controller.abort());
    requests.current.clear();
    pageLoading.current = {};
    setPages((old) => Object.fromEntries(Object.entries(old).map(([key, page]) => [key, { ...page, loading: false }])));
  }, []);

  const load = useCallback(async (name, cursor = null, explicit = false) => {
    if (!mounted.current || locks.current || pageLoading.current[name] || Date.now() < cooldownRef.current) return;
    if (explicit) {
      blockedRef.current = false;
      setError(null);
    }
    if (blockedRef.current) return;
    const version = (pageVersions.current[name] || 0) + 1;
    pageVersions.current[name] = version;
    const generation = epoch.current;
    const controller = new AbortController();
    requests.current.add(controller);
    pageLoading.current[name] = true;
    setPages((old) => ({ ...old, [name]: { ...old[name], loading: true, error: null } }));
    const current = () => mounted.current && generation === epoch.current && pageVersions.current[name] === version;
    try {
      const result = await readers[name](restaurantId, cursor, { signal: controller.signal });
      if (!current()) return;
      setPages((old) => {
        const items = cursor ? [...old[name].items, ...result.items] : result.items;
        return { ...old, [name]: { items: Array.from(new Map(items.map((item) => [item.id, item])).values()), next_cursor: result.next_cursor, loading: false, loaded: true, error: null } };
      });
    } catch (err) {
      if (!current() || controller.signal.aborted) return;
      const safe = report(err);
      setPages((old) => ({ ...old, [name]: { ...old[name], loading: false, error: safe } }));
    } finally {
      requests.current.delete(controller);
      if (current()) pageLoading.current[name] = false;
    }
  }, [restaurantId, report]);

  useEffect(() => {
    mounted.current = true;
    let active = true;
    // Recovery persistence may be synchronous or asynchronous. A storage failure
    // must not silently turn a retry into a new operation.
    Promise.resolve().then(() => pendingOperations.read(scope)).then((stored) => {
      if (!active) return;
      operationRef.current = stored;
      setOperation(stored);
      setRecoveryReady(true);
    }).catch(() => {
      if (active) report({ code: 'RECOVERY_STORAGE_UNAVAILABLE', message: 'Recovery storage is unavailable. Reopen tablet management before issuing a code.' });
    });
    load('users');
    load('devices');
    load('audit');
    return () => {
      active = false;
      mounted.current = false;
      epoch.current += 1;
      pairingRef.current = null;
      requests.current.forEach((controller) => controller.abort());
      requests.current.clear();
    };
  }, [scope, load, report]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (mounted.current) setNow(Date.now());
    }, 1000);
    const onVisibility = () => {
      const shown = document.visibilityState !== 'hidden';
      visibleRef.current = shown;
      setVisible(shown);
      if (!shown) {
        // Invalidate even a response already on the wire when the tab is hidden.
        invalidate();
        setPairing((old) => {
          const next = old ? { ...old, code: null } : null;
          pairingRef.current = next;
          return next;
        });
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [invalidate]);

  const clearOperation = useCallback(async () => {
    await pendingOperations.clear(scope);
    if (!mounted.current) return;
    operationRef.current = null;
    setOperation(null);
  }, [scope]);

  const refresh = useCallback(() => {
    load('users');
    load('devices');
    load('audit');
  }, [load]);

  // A synchronous ref lock closes the double-click gap before React re-renders.
  // Every write invalidates reads both before and after it, since the public
  // management responses have no ordering sequence.
  const mutate = useCallback(async (work) => {
    if (!mounted.current || locks.current || blockedRef.current || Date.now() < cooldownRef.current) return false;
    locks.current = true;
    setBusy(true);
    setError(null);
    setNotice(null);
    invalidate();
    const generation = epoch.current;
    const controller = new AbortController();
    requests.current.add(controller);
    const current = () => mounted.current && generation === epoch.current;
    try {
      await work({ signal: controller.signal }, current);
      return current();
    } catch (err) {
      if (current() && !controller.signal.aborted) report(err);
      return false;
    } finally {
      requests.current.delete(controller);
      locks.current = false;
      if (mounted.current) {
        setBusy(false);
        invalidate();
        refresh();
      }
    }
  }, [invalidate, refresh, report]);

  const issue = useCallback((body, recover = false) => mutate(async (options, current) => {
    // An older code/expiry must not clear or replace the operation now being sent.
    pairingRef.current = null;
    setPairing(null);
    let next = operationRef.current;
    if (!recover) {
      next = { key: createOperationId(), body: { associated_user_id: body.associated_user_id, display_name: body.display_name }, createdAt: Date.now() };
      await pendingOperations.write(scope, next);
      if (!current()) return;
      operationRef.current = next;
      setOperation(next);
    }
    if (!next) return;
    statusError.current = false;
    const result = await issueCode(restaurantId, next.body, next.key, options);
    if (!current()) return;
    const shown = { ...result, code: visibleRef.current && result.state === 'pending' && Date.parse(result.expires_at) > Date.now() ? result.code : null };
    pairingRef.current = shown;
    setPairing(shown);
    if (result.state !== 'pending') await clearOperation();
  }), [mutate, scope, restaurantId, clearOperation]);

  const cancel = useCallback(() => mutate(async (options, current) => {
    const target = pairingRef.current;
    if (!target) return;
    try {
      await cancelCode(restaurantId, target.pairing_id, options);
      if (!current()) return;
      const canceled = { ...target, code: null, state: 'canceled' };
      pairingRef.current = canceled;
      setPairing(canceled);
      await clearOperation();
      setNotice('Code canceled. No device was revoked.');
    } catch (err) {
      if (!current()) return;
      if (err.code !== 'PAIRING_ALREADY_REDEEMED') throw err;
      const redeemed = { ...target, code: null, state: 'redeemed', device_id: err.device_id };
      pairingRef.current = redeemed;
      setPairing(redeemed);
      await clearOperation();
      setNotice('The code was already redeemed. The device list has been refreshed; the device was not revoked.');
    }
  }), [mutate, restaurantId, clearOperation]);

  const poll = useCallback(async () => {
    const target = pairingRef.current;
    if (!target || target.state !== 'pending' || !target.code || !visibleRef.current || locks.current || blockedRef.current || statusError.current || Date.now() < cooldownRef.current) return;
    const generation = epoch.current;
    const controller = new AbortController();
    requests.current.add(controller);
    try {
      const result = await getStatus(restaurantId, target.pairing_id, { signal: controller.signal });
      if (!mounted.current || generation !== epoch.current || !visibleRef.current || pairingRef.current?.state !== 'pending' || Date.now() >= Date.parse(target.expires_at)) return;
      // Status never contains a code. Retain it only for the same pending view,
      // and never replace the original issuance expiry with a new deadline.
      const updated = { ...target, ...result, expires_at: target.expires_at, code: result.state === 'pending' && Date.now() < Date.parse(target.expires_at) ? target.code : null };
      pairingRef.current = updated;
      setPairing(updated);
      if (result.state !== 'pending') {
        await clearOperation();
        if (mounted.current && generation === epoch.current) {
          invalidate();
          refresh();
        }
      }
    } catch (err) {
      if (!mounted.current || generation !== epoch.current || controller.signal.aborted) return;
      statusError.current = true; // Explicit recovery, not a denied-write/read loop.
      report(err);
    } finally {
      requests.current.delete(controller);
    }
  }, [restaurantId, clearOperation, invalidate, refresh, report]);

  useEffect(() => {
    if (!visible || busy || pairing?.state !== 'pending' || !pairing?.code) return undefined;
    // Schedule from completion, not an interval: even slow requests cannot overlap.
    let active = true;
    let timer;
    const schedule = () => {
      timer = window.setTimeout(async () => {
        if (!active) return;
        await poll();
        if (active) schedule();
      }, 5000);
    };
    schedule();
    return () => { active = false; window.clearTimeout(timer); };
  }, [visible, busy, pairing?.pairing_id, pairing?.state, pairing?.code, poll]);

  useEffect(() => {
    if (pairing?.state !== 'pending' || now < Date.parse(pairing.expires_at)) return;
    const expired = { ...pairing, state: 'expired', code: null };
    pairingRef.current = expired;
    setPairing(expired);
    // The nonsecret operation remains recoverable if clearing storage fails.
    clearOperation().catch(() => report({ code: 'RECOVERY_STORAGE_UNAVAILABLE', message: 'The code expired. Recovery storage could not be cleared.' }));
  }, [pairing, now, clearOperation, report]);

  const rename = useCallback((id, name) => mutate(async (options, current) => {
    await renameDevice(restaurantId, id, name, options);
    if (current()) setNotice('Tablet nickname updated. The associated user has not changed.');
  }), [mutate, restaurantId]);

  const revoke = useCallback((id, reason, key) => mutate(async (options, current) => {
    await revokeDevice(restaurantId, id, reason, key, options);
    if (current()) setNotice('Tablet permanently revoked. Other tablets and personal sign-in are unchanged.');
  }), [mutate, restaurantId]);

  const closeCode = useCallback(() => {
    invalidate();
    setPairing((old) => {
      const next = old ? { ...old, code: null } : null;
      pairingRef.current = next;
      return next;
    });
  }, [invalidate]);

  return {
    pages, operation, recoveryReady, pairing, busy, error, notice, visible, now,
    blocked: blockedRef.current,
    retryIn: Math.max(0, Math.ceil((cooldownRef.current - now) / 1000)),
    issue, cancel, rename, revoke, load, closeCode,
    report,
  };
}
